/*
 *  Copyright (c) 2014 The WebRTC project authors. All Rights Reserved.
 *
 *  Use of this source code is governed by a BSD-style license
 *  that can be found in the https://github.com/webrtc/testrtc/blob/master/LICENSE.md
 */

export function MicTest(stream, resultCallback) {
  var AudioContext = window.AudioContext || window.webkitAudioContext;
  this.audioContext = new AudioContext();

  this.analyzeInProgress = false;
  this.testIsActive = true;
  this.inputChannelCount = 6;
  this.outputChannelCount = 2;
  this.bufferSize = 0;
  this.collectMilliseconds = 500;
  this.silentThreshold = 1 / 32767;
  this.lowVolumeThreshold = -60;
  this.monoDetectThreshold = 1 / 65536;
  this.clipCountThreshold = 6;
  this.clipThreshold = 1;

  this.start = () => {
    this.collectedAudio = [];

    for (var t = this.collectedSampleCount = 0; t < this.inputChannelCount; ++t) this.collectedAudio[t] = [];
    this.audioSource = this.audioContext.createMediaStreamSource(stream);
    this.scriptNode = this.audioContext.createScriptProcessor(this.bufferSize, this.inputChannelCount, this.outputChannelCount);
    this.audioSource.connect(this.scriptNode);
    this.scriptNode.connect(this.audioContext.destination);
    this.scriptNode.onaudioprocess = this.collectAudio;
    this.stopCollectingAudio = setTimeoutWithProgress(this.onStopCollectingAudio, this.collectMilliseconds * 2);
  };

  this.restart = () => {
    this.collectedAudio = [];

    for (var t = this.collectedSampleCount = 0; t < this.inputChannelCount; ++t) this.collectedAudio[t] = [];
    this.stopCollectingAudio = setTimeoutWithProgress(this.onStopCollectingAudio, this.collectMilliseconds * 2);
    this.analyzeInProgress = false;
  };

  this.stop = () => {
    this.testIsActive = false;
    this.audioSource.disconnect(this.scriptNode);
    this.scriptNode.disconnect(this.audioContext.destination);
  };

  this.collectAudio = (event) => {
    if (this.analyzeInProgress) return;

    // Simple silence detection: check first and last sample of each channel in
    // the buffer. If both are below a threshold, the buffer is considered
    // silent.
    var sampleCount = event.inputBuffer.length;
    var allSilent = true;
    for (var c = 0; c < event.inputBuffer.numberOfChannels; c++) {
      var data = event.inputBuffer.getChannelData(c);
      var first = Math.abs(data[0]);
      var last = Math.abs(data[sampleCount - 1]);
      var newBuffer;
      if (first > this.silentThreshold || last > this.silentThreshold) {
        // Non-silent buffers are copied for analysis. Note that the silent
        // detection will likely cause the stored stream to contain discontinu-
        // ities, but that is ok for our needs here (just looking at levels).
        newBuffer = new Float32Array(sampleCount);
        newBuffer.set(data);
        allSilent = false;
      } else {
        // Silent buffers are not copied, but we store empty buffers so that the
        // analysis doesn't have to care.
        newBuffer = new Float32Array();
      }
      this.collectedAudio[c].push(newBuffer);
    }
    if (!allSilent) {
      this.collectedSampleCount += sampleCount;
      if ((this.collectedSampleCount / event.inputBuffer.sampleRate) >= this.collectMilliseconds) {
        this.stopCollectingAudio();
      }
    }
  };

  this.onStopCollectingAudio = () => {
    this.analyzeInProgress = true;
    this.analyzeAudio(this.collectedAudio);
  };

  this.analyzeAudio = (channels) => {
    if (!this.testIsActive) return;

    var activeChannels = [];
    for (var c = 0; c < channels.length; c++) {
      if (this.channelStats(c, channels[c])) {
        activeChannels.push(c);
      }
    }
    if (activeChannels.length === 0) {
      console.debug('No active input channels detected. Microphone ' +
        'is most likely muted or broken, please check if muted in the ' +
        'sound settings or physically on the device.');
      resultCallback(false);
    } else {
      console.debug('Active audio input channels: ' + activeChannels.length);
      resultCallback(true);
    }
    this.restart();
  };

  this.channelStats = (channelNumber, buffers) => {
    var maxPeak = 0.0;
    var maxRms = 0.0;
    var clipCount = 0;
    var maxClipCount = 0;
    for (var j = 0; j < buffers.length; j++) {
      var samples = buffers[j];
      if (samples.length > 0) {
        var s = 0;
        var rms = 0.0;
        for (var i = 0; i < samples.length; i++) {
          s = Math.abs(samples[i]);
          maxPeak = Math.max(maxPeak, s);
          rms += s * s;
          if (maxPeak >= this.clipThreshold) {
            clipCount++;
            maxClipCount = Math.max(maxClipCount, clipCount);
          } else {
            clipCount = 0;
          }
        }
        // RMS is calculated over each buffer, meaning the integration time will
        // be different depending on sample rate and buffer size. In practise
        // this should be a small problem.
        rms = Math.sqrt(rms / samples.length);
        maxRms = Math.max(maxRms, rms);
      }
    }

    if (maxPeak > this.silentThreshold) {
      var dBPeak = this.dBFS(maxPeak);
      var dBRms = this.dBFS(maxRms);
      console.debug('Channel ' + channelNumber + ' levels: ' +
        dBPeak.toFixed(1) + ' dB (peak), ' + dBRms.toFixed(1) + ' dB (RMS)');
      if (dBRms < this.lowVolumeThreshold) {
        console.debug('Microphone input level is low, increase input ' +
          'volume or move closer to the microphone.');
      }
      if (maxClipCount > this.clipCountThreshold) {
        console.debug('Clipping detected! Microphone input level ' +
          'is high. Decrease input volume or move away from the microphone.');
      }
      return true;
    }
    return false;
  };

  this.dBFS = (gain) => {
    var dB = 20 * Math.log(gain) / Math.log(10);
    // Use Math.round to display up to one decimal place.
    return Math.round(dB * 10) / 10;
  };

  function setTimeoutWithProgress(timeoutCallback, timeoutMs) {
    var timeoutTask = function() {
      timeoutCallback();
    };
    var timer = setTimeout(timeoutTask, timeoutMs);
    var finish = function() {
      clearTimeout(timer);
      timeoutTask();
    };
    return finish;
  }
}